import { escapeHTML } from './billing'

// ─── Prescriber resolution & prescription output ─────────────────────────────
// Doctor credentials come only from user_profiles; clinic details only from clinics.

const normName = s => String(s || '').toLowerCase().replace(/^dr\.?\s*/, '').replace(/\s+/g, ' ').trim()

export function findDoctor(doctors, name) {
  const n = normName(name)
  if (!n) return null
  return (doctors || []).find(d => normName(d.full_name) === n) || null
}

// The logged-in user if they hold a registration number, otherwise the doctor
// chosen on the prescription/appointment (or the only registered doctor).
export function resolvePrescriber(userProfile, doctors, chosenName) {
  if (userProfile?.registration_no) return userProfile
  return findDoctor(doctors, chosenName) || ((doctors || []).length === 1 ? doctors[0] : null)
}

export function regLine(doc) {
  if (!doc?.registration_no) return ''
  return `Reg. No. ${doc.registration_no}${doc.registration_council ? ` (${doc.registration_council})` : ''}`
}

export function fmtRxDate(d) {
  const date = d ? new Date(String(d).length <= 10 ? d + 'T00:00:00' : d) : new Date()
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}

// medicines: [{ name, formulation, dosage, duration, instructions }]
export function buildPrescriptionPrintHTML({ clinic, doctor, patient = {}, date, complaint, findings, diagnosis, medicines = [], advice = [], followUp, notes }) {
  const c   = clinic || {}
  const d   = doctor || {}
  const reg = regLine(d)
  const meds = medicines.filter(m => m?.name)
  const medRows = meds.map((m, i) => `<tr><td>${i + 1}.</td><td><strong>${escapeHTML(m.name)}</strong>${m.formulation ? ' (' + escapeHTML(m.formulation) + ')' : ''}</td><td>${escapeHTML(m.dosage || '—')}</td><td>${escapeHTML(m.duration || '—')}</td><td>${escapeHTML(m.instructions || '—')}</td></tr>`).join('')
  const contact = [c.phone && `Ph: ${escapeHTML(c.phone)}`, c.email && escapeHTML(c.email)].filter(Boolean).join(' &nbsp;|&nbsp; ')
  const section = (title, body) => body ? `<section><div class="stitle">${title}</div>${body}</section>` : ''

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Prescription — ${escapeHTML(patient.name || '')}</title>
<style>
  @page{size:A4;margin:12mm}
  *{box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;max-width:720px;margin:0 auto;padding:28px;color:#1e293b;font-size:13px}
  .hdr{display:flex;justify-content:space-between;gap:24px;border-bottom:3px double #148080;padding-bottom:14px;margin-bottom:14px}
  .doc{font-size:20px;font-weight:800;color:#0d3d3d}
  .qual{font-size:12.5px;color:#126868;font-weight:600;margin-top:2px}
  .reg{font-size:11.5px;color:#475569;margin-top:2px}
  .clinic{text-align:right;font-size:11.5px;color:#64748b;line-height:1.55;max-width:320px}
  .clinic strong{display:block;font-size:15px;color:#0d3d3d}
  .clinic .tagline{display:block;font-size:11.5px;font-weight:400;color:#148080;margin-bottom:1px}
  .ptrow{display:flex;justify-content:space-between;background:#edfafa;border:1px solid #ccf2f2;border-radius:6px;padding:8px 12px;margin-bottom:14px}
  section{margin-bottom:12px}
  .stitle{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#64748b;margin-bottom:4px}
  .rx{font-size:30px;font-family:Georgia,serif;font-style:italic;color:#126868;line-height:1}
  table{width:100%;border-collapse:collapse;margin-top:6px}
  th{background:#0f5454;color:#fff;padding:6px 8px;text-align:left;font-size:10.5px;text-transform:uppercase}
  td{padding:6px 8px;border-bottom:1px solid #f1f5f9;font-size:12.5px;vertical-align:top}
  .sig{margin-top:70px;display:flex;justify-content:flex-end;page-break-inside:avoid}
  .sigbox{width:240px;text-align:center;border-top:1px solid #334155;padding-top:6px;font-size:12px}
  .sigbox .n{font-weight:700;color:#0f172a}
  .sigbox .s{font-size:11px;color:#64748b}
  @media print{body{padding:0}}
</style></head><body>
<div class="hdr">
  <div>
    <div class="doc">${escapeHTML(d.full_name || '')}</div>
    ${d.qualification ? `<div class="qual">${escapeHTML(d.qualification)}</div>` : ''}
    ${reg ? `<div class="reg">${escapeHTML(reg)}</div>` : ''}
  </div>
  <div class="clinic">
    <strong>${escapeHTML(c.name || '')}</strong>
    ${c.tagline ? `<span class="tagline">${escapeHTML(c.tagline)}</span>` : ''}
    ${c.address ? escapeHTML(c.address) + '<br>' : ''}${contact}
  </div>
</div>
<div class="ptrow">
  <div><strong>${escapeHTML(patient.name || '—')}</strong>${patient.age ? ' · ' + escapeHTML(String(patient.age)) + ' yrs' : ''}${patient.gender ? ' · ' + escapeHTML(patient.gender) : ''}</div>
  <div>Date: <strong>${fmtRxDate(date)}</strong></div>
</div>
${section('Chief Complaint', complaint && escapeHTML(complaint))}
${section('Examination Findings', findings && escapeHTML(findings))}
${section('Diagnosis', diagnosis && `<strong>${escapeHTML(diagnosis)}</strong>`)}
<section><div class="rx">℞</div>
  <table><tr><th>#</th><th>Medicine</th><th>Dosage</th><th>Duration</th><th>Instructions</th></tr>${medRows || '<tr><td colspan="5" style="color:#94a3b8">—</td></tr>'}</table>
</section>
${section('Advice', advice.length && `<ul style="margin:2px 0;padding-left:18px">${advice.map(a => '<li>' + escapeHTML(a) + '</li>').join('')}</ul>`)}
${section('Follow-up', followUp && escapeHTML(followUp))}
${section('Additional Notes', notes && escapeHTML(notes))}
<div class="sig"><div class="sigbox">
  <div class="n">${escapeHTML(d.full_name || '')}</div>
  ${d.qualification ? `<div class="s">${escapeHTML(d.qualification)}</div>` : ''}
  ${reg ? `<div class="s">${escapeHTML(reg)}</div>` : ''}
</div></div>
<script>window.onload=()=>window.print()</script>
</body></html>`
}

export function printPrescription(opts) {
  const win = window.open('', '_blank')
  if (!win) { alert('Please allow pop-ups to print the prescription.'); return }
  win.document.write(buildPrescriptionPrintHTML(opts))
  win.document.close()
}

export function buildRxWhatsApp({ clinic, doctor, patient = {}, date, diagnosis, medicines = [], advice = [], followUp, notes }) {
  const c = clinic || {}
  const d = doctor || {}
  return [
    `*${c.name || 'Prescription'} — Prescription*`,
    c.tagline ? `_${c.tagline}_` : null,
    d.full_name ? `${d.full_name}${d.qualification ? ` | ${d.qualification}` : ''}` : null,
    regLine(d) || null,
    ``,
    `*Patient:* ${patient.name || '—'}${patient.age ? `  |  Age: ${patient.age}` : ''}  |  Date: ${fmtRxDate(date)}`,
    `*Diagnosis:* ${diagnosis || '—'}`,
    ``,
    `*Rx:*`,
    ...medicines.filter(m => m?.name).map((m, i) => `${i + 1}. ${m.name}${m.formulation ? ` (${m.formulation})` : ''}\n   ${[m.dosage, m.duration, m.instructions].filter(Boolean).join(' | ')}`),
    advice.length ? `\n*Advice:*\n${advice.map(a => `• ${a}`).join('\n')}` : null,
    followUp ? `*Follow-up:* ${followUp}` : null,
    notes ? `\n${notes}` : null,
    ``,
    [c.name, c.address].filter(Boolean).join(', ') ? `_${[c.name, c.address].filter(Boolean).join(', ')}_` : null,
    c.phone ? `_Ph: ${c.phone}_` : null,
  ].filter(v => v !== null).join('\n')
}
